import type {
  ButtonProps,
  ListItemButtonProps,
  ListItemProps,
  MenuItemProps,
  LinkProps as MuiLinkProps,
  TabProps,
} from '@mui/material';
import { Button, ListItem, ListItemButton, MenuItem, Link as MuiLink, Tab } from '@mui/material';
import { forwardRef } from 'react';
import {
  Link as RouterLink,
  createPath,
  type LinkProps as RouterLinkProps,
} from 'react-router-dom';
import { IconButton, type IconButtonProps } from '~/components/IconButton';

export type LinkProps<T = MuiLinkProps> = Omit<T, 'href' | 'button' | 'component'> &
  RouterLinkProps;

/** The theme's ButtonBase `LinkComponent`, so any button given an `href` navigates in-app. */
export const LinkBehavior = forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }
>(function LinkBehavior({ href, ...props }, ref) {
  return <RouterLink {...props} ref={ref} to={href} />;
});

export const Link = forwardRef<HTMLAnchorElement, LinkProps<MuiLinkProps>>(
  //
  function Link(props, ref) {
    return <MuiLink {...props} component={RouterLink} ref={ref} />;
  },
);

export const ButtonLink = forwardRef<HTMLAnchorElement, LinkProps<ButtonProps>>(
  //
  function ButtonLink(props, ref) {
    return (
      <Button
        {...props}
        component={RouterLink}
        ref={ref}
        sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...props.sx }}
      />
    );
  },
);

export const IconButtonLink = forwardRef<HTMLAnchorElement, LinkProps<IconButtonProps>>(
  // IconButton takes no `component`; LinkBehavior turns its href into a router link.
  function IconButtonLink({ to, ...props }, ref) {
    return (
      <IconButton
        {...props}
        href={typeof to === 'string' ? to : createPath(to)}
        ref={ref}
        size="large"
      />
    );
  },
);

export const ListItemLink = forwardRef<HTMLAnchorElement, LinkProps<ListItemProps<'a'>>>(
  function ListItemLink(props, ref) {
    return <ListItem {...props} component={RouterLink} ref={ref} />;
  },
);

export const ListItemButtonLink = forwardRef<
  HTMLAnchorElement,
  LinkProps<ListItemButtonProps<'a'>>
>(function ListItemButtonLink(props, ref) {
  return <ListItemButton {...props} component={RouterLink} ref={ref} />;
});

export const MenuItemLink = forwardRef<HTMLAnchorElement, LinkProps<MenuItemProps<'a'>>>(
  function MenuItemLink(props, ref) {
    return <MenuItem {...props} component={RouterLink} ref={ref} />;
  },
);

export type TabLinkProps = LinkProps<TabProps<'a'>>;
export const TabLink = forwardRef<HTMLAnchorElement, TabLinkProps>(
  //
  function TabLink(props, ref) {
    return <Tab {...props} component={RouterLink} ref={ref} />;
  },
);
